// askSmart-ui/src/pages/TOSP/tabs/TransferTab.tsx
import React, { useEffect, useState } from "react";
import { useAuth } from "../../../hooks/useAuth";
import { useTospData } from "../TospDataContext";
import { tenantLabel } from "../types";
import { moveTenancyContact, OrgUser, fetchOrgUsers } from "../tenancyTransfer";
import { useUsersWithModuleAccess } from "../../../hooks/useUsersWithModuleAccess";

/**
 * Handing a tenancy from one person to another.
 *
 * Starts from the TENANCY, not from the user, because one person can hold
 * several — the property manager routinely holds every tenancy she is setting
 * up until its tenant has an account, and a transfer names one of them.
 *
 * This moves who the tenancy BELONGS to: the contact on the record, and the
 * SMRT Docs assignment that lets them open their lease. Whether the documents
 * follow is asked rather than assumed — a manager handing over a tenancy she
 * set up was only ever holding the tenant's own lease, while a business
 * changing hands leaves the outgoing party's executed lease where it belongs.
 */
export default function TransferTab() {
  const { tenants, saveTenant } = useTospData();
  const { user } = useAuth();

  const [tenancyId, setTenancyId] = useState("");
  const [incomingId, setIncomingId] = useState("");
  /** Whether the paperwork follows. On by default: the handover case is the
   *  common one, and a business changing hands is the exception. */
  const [withDocuments, setWithDocuments] = useState(true);
  const [users, setUsers] = useState<OrgUser[]>([]);
  const [usersError, setUsersError] = useState<string | null>(null);
  // Only people who can actually use a tenancy once they have it. Transferring
  // to a member without TOSP access succeeded and then handed them a portal
  // that answers "Insufficient permissions" — a transfer that looked done and
  // wasn't. Owner counts; so does any role carrying the module's access keys.
  const tospUsers = useUsersWithModuleAccess("/tosp");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchOrgUsers()
      .then(setUsers)
      .catch(() => setUsersError("Couldn't load the user list. Reload to try again."));
  }, []);

  const tenancy = tenants.find((candidate) => candidate.id === tenancyId) || null;
  const incoming = users.find((user) => user.userId === incomingId) || null;

  // Offered as transferees. `users` stays the full list, because the OUTGOING
  // holder still has to be identifiable even if their access was removed.
  const eligible = React.useMemo(() => {
    const allowed = new Set(tospUsers.map((user) => user.id));
    return users.filter((user) => allowed.has(user.userId));
  }, [users, tospUsers]);

  // Whoever holds it now, matched the same way everything else matches a
  // tenancy to a person: on the contact email.
  const outgoing =
    tenancy &&
    users.find(
      (user) =>
        user.email.trim().toLowerCase() ===
        (tenancy.primaryContactEmail || "").trim().toLowerCase(),
    );

  const sameParty =
    Boolean(tenancy && incoming) &&
    incoming!.email.trim().toLowerCase() ===
      (tenancy!.primaryContactEmail || "").trim().toLowerCase();

  const ready = Boolean(tenancy && incoming) && !sameParty && !busy;

  const transfer = async () => {
    if (!tenancy || !incoming || !ready) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const moved = await moveTenancyContact(tenancy, incoming, saveTenant, {
        outgoing: outgoing || null,
        withDocuments,
        // Who did it, taken from the session rather than inferred afterwards
        // from whatever the record ended up saying.
        byEmail: user?.email || "",
        byName: user?.name || "",
      });
      setResult(
        `${tenantLabel(tenancy)} now belongs to ${incoming.name || incoming.email}.` +
          (moved.serviceMoved
            ? " The SMRT Docs service moved with it."
            : " No SMRT Docs service was recorded on this tenancy, so nothing was reassigned there.") +
          (withDocuments
            ? moved.documentsMoved > 0
              ? ` ${moved.documentsMoved} document${moved.documentsMoved === 1 ? "" : "s"} moved to their record.`
              : " There were no documents on it to move."
            : " Documents stayed on the outgoing holder's record."),
      );
      setIncomingId("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "The transfer didn't complete.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="tosp-panel">
      <h2 className="tosp-panel-title">Transfer a Tenancy</h2>
      <p className="tosp-panel-note">
        Moves who a tenancy belongs to — the contact on the record, and the SMRT
        Docs service holding their lease. Reported sales stay with the business.
      </p>

      {usersError && (
        <div className="tosp-notice tosp-notice-error" role="alert">
          {usersError}
        </div>
      )}

      <div className="tosp-field">
        <label className="tosp-label" htmlFor="tosp-transfer-tenancy">
          Tenancy
        </label>
        <select
          id="tosp-transfer-tenancy"
          className="manage-input"
          value={tenancyId}
          onChange={(e) => {
            setTenancyId(e.target.value);
            setResult(null);
            setError(null);
          }}
        >
          <option value="">— Select a tenancy —</option>
          {[...tenants]
            .sort((a, b) => tenantLabel(a).localeCompare(tenantLabel(b)))
            .map((candidate) => (
              <option key={candidate.id} value={candidate.id}>
                {tenantLabel(candidate)}
              </option>
            ))}
        </select>
      </div>

      {tenancy && (
        <div className="tosp-field">
          <span className="tosp-label">Currently held by</span>
          <div className="tosp-readonly">
            {outgoing?.name || tenancy.primaryContactName || "—"}
            {tenancy.primaryContactEmail ? ` — ${tenancy.primaryContactEmail}` : ""}
            {/* Said plainly: the contact on the record may be somebody who has
                since left the org, and the transfer still needs to work. */}
            {tenancy.primaryContactEmail && !outgoing
              ? " (no matching account)"
              : ""}
          </div>
        </div>
      )}

      <div className="tosp-field">
        <label className="tosp-label" htmlFor="tosp-transfer-incoming">
          Transfer to
        </label>
        <select
          id="tosp-transfer-incoming"
          className="manage-input"
          value={incomingId}
          disabled={!tenancy}
          onChange={(e) => {
            setIncomingId(e.target.value);
            setResult(null);
            setError(null);
          }}
        >
          <option value="">— Select a user —</option>
          {/* Only members with TOSP access — see `eligible` above. */}
          {eligible.map((user) => (
            <option key={user.userId} value={user.userId}>
              {user.name ? `${user.name} — ${user.email}` : user.email}
            </option>
          ))}
        </select>
      </div>

      {sameParty && (
        <div className="tosp-notice" role="status">
          That's who holds it already.
        </div>
      )}

      {tenancy && incoming && !sameParty && (
        <>
          {/* The one question the answer depends on, asked plainly. Handing
              over a tenancy you set up and a business changing hands are the
              same mechanism and opposite outcomes for the paperwork. */}
          <div className="tosp-field tosp-transfer-choice">
            <label className="tosp-checkbox">
              <input
                type="checkbox"
                checked={withDocuments}
                onChange={(e) => setWithDocuments(e.target.checked)}
              />
              <span>Move the lease and documents to the incoming holder</span>
            </label>
            <p className="tosp-panel-note">
              {withDocuments
                ? "For a tenancy you were holding on the tenant's behalf — the lease was always theirs."
                : "For a business changing hands — the outgoing party's executed lease stays on their record, and the incoming holder starts with empty document slots."}
            </p>
          </div>

          <div className="tosp-transfer-summary">
            <strong>What this will do</strong>
            <ul>
              <li>
                Set the contact on {tenantLabel(tenancy)} to{" "}
                {incoming.name || incoming.email}, which gives them the portal,
                their sales and their lease.
              </li>
              <li>
                {tenancy.smrtDocsServiceId
                  ? "Move the SMRT Docs service holding the lease to them, and take it from the current holder."
                  : "Leave SMRT Docs alone — no service is recorded on this tenancy yet."}
              </li>
              <li>
                Leave the reported sales where they are. They belong to the
                business, not to the person reporting them.
              </li>
              <li>
                {withDocuments
                  ? `Move the documents from ${outgoing?.name || tenancy.primaryContactEmail || "the current holder"}'s record to theirs.`
                  : `Leave signed documents on ${outgoing?.name || tenancy.primaryContactEmail || "the outgoing holder"}'s record.`}
              </li>
              <li>
                Record{" "}
                {tenancy.primaryContactEmail || "the current holder"} as the
                previous holder, so this can be traced later.
              </li>
            </ul>
          </div>
        </>
      )}

      {error && (
        <div className="tosp-notice tosp-notice-error" role="alert">
          {error}
        </div>
      )}
      {result && (
        <div className="tosp-notice tosp-notice-ok" role="status">
          {result}
        </div>
      )}

      <div className="tosp-panel-actions">
        <button
          type="button"
          className="tosp-btn-primary"
          disabled={!ready}
          onClick={transfer}
        >
          {busy ? "Transferring…" : "Transfer Tenancy"}
        </button>
      </div>
    </div>
  );
}
